import React from 'react';
import { useSystemRoles } from '@/hooks/useSystemRoles';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Building2, Building, Users, Shield } from 'lucide-react';

export type CompetitionScope = 'branch' | 'company' | 'commercial_group';

interface CompetitionScopeSelectorProps {
  value: CompetitionScope;
  onChange: (scope: CompetitionScope) => void;
  className?: string;
}

export function CompetitionScopeSelector({ value, onChange, className }: CompetitionScopeSelectorProps) {
  const { isSuperAdmin } = useSystemRoles();

  const scopeOptions = [
    {
      value: 'branch' as CompetitionScope,
      label: 'Por Sucursal',
      description: 'Competir solo con vendedores de mi sucursal',
      icon: Building2,
      color: 'text-status-info'
    },
    {
      value: 'company' as CompetitionScope,
      label: 'Por Empresa',
      description: 'Competir con todos los vendedores de la empresa',
      icon: Building,
      color: 'text-status-success'
    },
    ...(isSuperAdmin ? [{
      value: 'commercial_group' as CompetitionScope,
      label: 'Por Grupo Comercial',
      description: 'Competir entre todas las empresas del grupo',
      icon: Users,
      color: 'text-primary'
    }] : [])
  ];

  const currentOption = scopeOptions.find(opt => opt.value === value);

  return (
    <div className={className}>
      <div className="flex items-center gap-2 mb-2">
        <Shield className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium text-muted-foreground">Alcance de Competencia</span>
      </div>
      
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-full">
          <SelectValue>
            {currentOption && (
              <div className="flex items-center gap-2">
                <currentOption.icon className={`h-4 w-4 ${currentOption.color}`} />
                <span>{currentOption.label}</span>
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {scopeOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              <div className="flex items-center gap-3 py-1">
                <option.icon className={`h-4 w-4 ${option.color}`} />
                <div>
                  <div className="font-medium">{option.label}</div>
                  <div className="text-xs text-muted-foreground">{option.description}</div>
                </div>
                {option.value === 'commercial_group' && (
                  <div title="Solo Super Admin">
                    <Shield className="h-3 w-3 text-primary ml-auto" />
                  </div>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}